import React, { useMemo } from 'react';
import { HoverTip } from '../billing/SupplierBillHover';
import type { PurchaseRequisition } from '../../types/purchasing';
import {
  summarizePurchaseStatHover,
  type PurchaseStatHoverKind,
  type PurchaseStatHoverMonth,
} from '../../utils/purchaseStatHover';

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function PurchaseStatSparkline({ months }: { months: PurchaseStatHoverMonth[] }) {
  const w = 148;
  const h = 28;
  const pad = 3;
  const values = months.map((m) => m.amount);
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const coords = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (w - pad * 2);
    const y = pad + (1 - (v - min) / span) * (h - pad * 2);
    return `${x},${y}`;
  });
  return (
    <svg className="po-stat-sparkline" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={coords.join(' ')}
      />
    </svg>
  );
}

export function PurchaseStatHover({
  kind,
  rows,
  children,
}: {
  kind: PurchaseStatHoverKind;
  rows: PurchaseRequisition[];
  children: React.ReactNode;
}) {
  const summary = useMemo(() => summarizePurchaseStatHover(rows, kind), [rows, kind]);
  const emptyLabel = kind === 'issued' ? 'No POs issued' : 'None purchased yet';
  const rangeLabel =
    summary.months.length > 0
      ? `${summary.months[0].label} – ${summary.months[summary.months.length - 1].label}`
      : '';

  const tip =
    summary.count === 0 ? (
      <div className="po-stat-tip">{emptyLabel}</div>
    ) : (
      <div className="po-stat-tip">
        <div className="po-stat-tip-total">{money(summary.total)}</div>
        <ul className="po-stat-tip-classes">
          {summary.byClass.map((row) => (
            <li key={row.classId}>
              <span>{row.label}</span>
              <span>{money(row.amount)}</span>
            </li>
          ))}
        </ul>
        <PurchaseStatSparkline months={summary.months} />
        {rangeLabel ? <div className="po-stat-tip-range">{rangeLabel}</div> : null}
      </div>
    );

  return (
    <HoverTip tip={tip}>
      {children}
    </HoverTip>
  );
}
